
import { Link } from "react-router-dom";
import { MapPin, Phone } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import NewsletterForm from "./NewsletterForm";
import { supabase } from "@/integrations/supabase/client";
import { useTranslatedContent } from "@/hooks/useTranslatedContent";
import { useLanguage } from "@/contexts/LanguageContext";
import { getLocalizedPath } from "./LocalizedRoute";

const WHATSAPP_LOGO_URL =
  "https://kxrduwrurqjvouiqobyc.supabase.co/storage/v1/object/public/photo-edm//WhatsApp.png";

const Footer = () => {
  const currentYear = new Date().getFullYear();
  const { language, t } = useLanguage();

  const content = {
    description:
      "Une villa d'exception au Grau du Roi, offrant confort et élégance pour des vacances inoubliables sur la côte méditerranéenne.",
    quickLinks: "Navigation",
    home: "Accueil",
    gallery: "Galerie",
    equipment: "Équipements",
    surroundings: "Les environs",
    book: "Réserver",
    contact: "Contact",
    contactTitle: "Contact",
    newsletter: "Newsletter",
    newsletterDesc: "Offres exclusives et actualités de la villa.",
    allRights: "Tous droits réservés.",
    legalNotice: "Mentions légales",
    privacyPolicy: "Confidentialité",
  };

  const { translatedContent } = useTranslatedContent(content);

  const { data: admins } = useQuery({
    queryKey: ["contact-admins"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("profiles")
        .select("*")
        .eq("display_on_contact", true);
      if (error) throw error;
      return data;
    },
  });

  const getPath = (frenchPath: string) =>
    language === "fr" ? frenchPath : getLocalizedPath(frenchPath, language);

  const navLinks = [
    { to: "/", label: translatedContent.home || content.home },
    { to: "/galerie", label: translatedContent.gallery || content.gallery },
    { to: "/equipements", label: translatedContent.equipment || content.equipment },
    { to: "/environs", label: translatedContent.surroundings || content.surroundings },
    { to: "/reserver", label: translatedContent.book || content.book },
    { to: "/contact", label: translatedContent.contact || content.contact },
  ];

  return (
    <footer className="bg-[#0F1923] text-white">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 pt-16 md:pt-20 pb-8">

        {/* Main grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10 mb-14">

          {/* Brand */}
          <div className="sm:col-span-2 lg:col-span-1">
            <Link to="/" className="inline-block mb-5">
              <span className="font-playfair font-bold text-2xl text-white">
                Douce<span className="text-villa-turquoise-light">Vague</span>
              </span>
            </Link>
            <p className="text-sm text-white/50 leading-relaxed max-w-xs">
              {translatedContent.description || content.description}
            </p>
          </div>

          {/* Nav links */}
          <div>
            <h4 className="text-xs font-semibold tracking-[0.15em] uppercase text-white/30 mb-5">
              {translatedContent.quickLinks || content.quickLinks}
            </h4>
            <ul className="space-y-3">
              {navLinks.map((link) => (
                <li key={link.to}>
                  <Link
                    to={getPath(link.to)}
                    className="text-sm text-white/55 hover:text-white transition-colors duration-200"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="text-xs font-semibold tracking-[0.15em] uppercase text-white/30 mb-5">
              {translatedContent.contactTitle || content.contactTitle}
            </h4>
            <ul className="space-y-4">
              <li className="flex items-start gap-2.5">
                <MapPin className="h-4 w-4 text-villa-turquoise-light flex-shrink-0 mt-0.5" strokeWidth={1.5} />
                <span className="text-sm text-white/55 leading-relaxed">
                  Villa Douce Vague<br />Le Grau du Roi, France
                </span>
              </li>
              {admins?.map((admin) => (
                <li key={admin.id} className="flex items-center gap-3">
                  <Avatar className="w-8 h-8 flex-shrink-0">
                    <AvatarImage
                      src={
                        admin.profile_image
                          ? `${supabase.storage.from("profile_images").getPublicUrl(admin.profile_image).data.publicUrl}?width=64&height=64&resize=cover&quality=80`
                          : undefined
                      }
                      alt={`${admin.first_name} ${admin.last_name}`}
                      loading="lazy"
                    />
                    <AvatarFallback className="bg-villa-turquoise/20 text-villa-turquoise-light text-xs">
                      {admin.first_name?.[0]}{admin.last_name?.[0]}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-white/80 font-medium truncate">
                      {admin.first_name} {admin.last_name}
                    </p>
                    {admin.phone && (
                      <a
                        href={`tel:${admin.phone}`}
                        className="text-xs text-villa-turquoise-light hover:text-white transition-colors flex items-center gap-1"
                      >
                        <Phone className="h-3 w-3" strokeWidth={1.5} />
                        {admin.phone}
                      </a>
                    )}
                  </div>
                  {admin.whatsapp_contact_url && (
                    <a
                      href={admin.whatsapp_contact_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      title={`WhatsApp — ${admin.first_name}`}
                      className="flex-shrink-0"
                    >
                      <img
                        src={WHATSAPP_LOGO_URL}
                        alt="WhatsApp"
                        className="w-7 h-7 rounded-full object-contain opacity-80 hover:opacity-100 transition-opacity"
                        loading="lazy"
                      />
                    </a>
                  )}
                </li>
              ))}
            </ul>
          </div>

          {/* Newsletter */}
          <div>
            <h4 className="text-xs font-semibold tracking-[0.15em] uppercase text-white/30 mb-5">
              {translatedContent.newsletter || content.newsletter}
            </h4>
            <p className="text-sm text-white/50 mb-4 leading-relaxed">
              {translatedContent.newsletterDesc || content.newsletterDesc}
            </p>
            <NewsletterForm />
          </div>
        </div>

        {/* Bottom bar */}
        <div className="border-t border-white/[0.07] pt-6 flex flex-col sm:flex-row items-center justify-between gap-3">
          <p className="text-xs text-white/30 order-2 sm:order-1">
            © {currentYear} Villa Douce Vague. {translatedContent.allRights || content.allRights}
          </p>
          <div className="flex items-center gap-5 order-1 sm:order-2">
            <Link
              to={getPath("/mentions-legales")}
              className="text-xs text-white/30 hover:text-white/60 transition-colors"
            >
              {translatedContent.legalNotice || content.legalNotice}
            </Link>
            <Link
              to={getPath("/mentions-legales")}
              className="text-xs text-white/30 hover:text-white/60 transition-colors"
            >
              {translatedContent.privacyPolicy || content.privacyPolicy}
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
